"use client";

import { INPUT_MONO, TABLE, TD } from "@/components/tool-kit/styles";
import { STAT_TH as TH } from "@/components/tools/stats-tests/kit";
import { t } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import type { NumberFormatter } from "@/lib/tool-kit/stats-tests/format";

/*
 * An editable contingency table of counts, with row and column totals — the input of
 * the chi-square, Fisher and McNemar calculators. Every word arrives through props.
 * Labels are editable when the caller passes the label handlers (chi-square), fixed
 * otherwise (the 2 × 2 tests, whose rows and columns have set meanings).
 */

const LABEL_INPUT =
  "w-full min-w-[6rem] rounded-md border border-border bg-card px-2 py-1 font-medium text-foreground text-xs focus:outline-none focus:ring-2 focus:ring-primary";

export interface CountGridText {
  /** "Count in row {row}, column {column}". */
  cell: string;
  /** "Name of row {n}". */
  rowName: string;
  /** "Name of column {n}". */
  columnName: string;
  total: string;
}

function total(values: readonly string[]): string | number {
  let sum = 0;
  for (const value of values) {
    const parsed = parseIntegerInput(value);
    if (parsed === null || parsed < 0) {
      return "—";
    }
    sum += parsed;
  }
  return sum;
}

function Header({
  id,
  value,
  label,
  onChange,
}: {
  id: string;
  value: string;
  label: string;
  onChange?: (value: string) => void;
}) {
  if (!onChange) {
    return <>{value}</>;
  }
  return (
    <>
      <label className="sr-only" htmlFor={id}>
        {label}
      </label>
      <input
        className={LABEL_INPUT}
        id={id}
        onChange={(event) => onChange(event.target.value)}
        type="text"
        value={value}
      />
    </>
  );
}

export function CountGrid({
  idPrefix,
  rowLabels,
  colLabels,
  values,
  onCell,
  onRowLabel,
  onColLabel,
  text,
  fmt,
}: {
  idPrefix: string;
  rowLabels: readonly string[];
  colLabels: readonly string[];
  values: readonly (readonly string[])[];
  onCell: (row: number, col: number, value: string) => void;
  onRowLabel?: (row: number, value: string) => void;
  onColLabel?: (col: number, value: string) => void;
  text: CountGridText;
  fmt: NumberFormatter;
}) {
  const show = (value: string | number) =>
    typeof value === "number" ? fmt(value) : value;
  const columnTotals = colLabels.map((_, j) =>
    total(values.map((row) => row[j] ?? ""))
  );
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className={TABLE}>
        <thead>
          <tr>
            <td className={TD} />
            {colLabels.map((label, j) => (
              <th className={TH} key={`${idPrefix}-col-${j + 1}`} scope="col">
                <Header
                  id={`${idPrefix}-col-${j + 1}`}
                  label={t(text.columnName, { n: j + 1 })}
                  onChange={onColLabel ? (v) => onColLabel(j, v) : undefined}
                  value={label}
                />
              </th>
            ))}
            <th className={TH} scope="col">
              {text.total}
            </th>
          </tr>
        </thead>
        <tbody>
          {values.map((row, i) => (
            <tr key={`${idPrefix}-row-${i + 1}`}>
              <th className={TH} scope="row">
                <Header
                  id={`${idPrefix}-row-${i + 1}`}
                  label={t(text.rowName, { n: i + 1 })}
                  onChange={onRowLabel ? (v) => onRowLabel(i, v) : undefined}
                  value={rowLabels[i] ?? ""}
                />
              </th>
              {row.map((value, j) => {
                const id = `${idPrefix}-cell-${i + 1}-${j + 1}`;
                return (
                  <td className={TD} key={id}>
                    <label className="sr-only" htmlFor={id}>
                      {t(text.cell, {
                        row: rowLabels[i] || i + 1,
                        column: colLabels[j] || j + 1,
                      })}
                    </label>
                    <input
                      className={`${INPUT_MONO} min-w-[4.5rem]`}
                      id={id}
                      inputMode="numeric"
                      onChange={(event) => onCell(i, j, event.target.value)}
                      type="text"
                      value={value}
                    />
                  </td>
                );
              })}
              <td className={`${TD} font-mono text-muted-foreground`}>
                {show(total(row))}
              </td>
            </tr>
          ))}
          <tr>
            <th className={TH} scope="row">
              {text.total}
            </th>
            {columnTotals.map((value, j) => (
              <td
                className={`${TD} font-mono text-muted-foreground`}
                key={`${idPrefix}-total-${j + 1}`}
              >
                {show(value)}
              </td>
            ))}
            <td className={`${TD} font-mono text-muted-foreground`}>
              {show(total(values.flat()))}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

/** Parse every cell as a non-negative whole number, or null if any is not one. */
export function parseCounts(
  values: readonly (readonly string[])[]
): number[][] | null {
  const table: number[][] = [];
  for (const row of values) {
    const parsed: number[] = [];
    for (const value of row) {
      const count = parseIntegerInput(value);
      if (count === null || count < 0) {
        return null;
      }
      parsed.push(count);
    }
    table.push(parsed);
  }
  return table;
}

/** A copy of a list with one item replaced. */
export function replaceAt(
  list: readonly string[],
  index: number,
  value: string
): string[] {
  return list.map((item, i) => (i === index ? value : item));
}

/** A copy of a grid with one cell replaced. */
export function replaceCell(
  grid: readonly (readonly string[])[],
  row: number,
  col: number,
  value: string
): string[][] {
  return grid.map((cells, i) =>
    i === row ? replaceAt(cells, col, value) : [...cells]
  );
}
